import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import AuthContext from '../../../contexts/AuthContext';
import { supabase } from '../../../utils/supabase';

// Activity Item Skeleton Component
const ActivityItemSkeleton = ({ delay = 0 }) => {
  return (
    <div 
      className="p-3 rounded-lg border border-gray-100 animate-pulse"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-start space-x-3">
        {/* Icon skeleton */}
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex-shrink-0"></div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            {/* Title skeleton */}
            <div className="w-32 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
            {/* Time skeleton */}
            <div className="w-12 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded flex-shrink-0"></div>
          </div>
          
          {/* Description skeleton */}
          <div className="w-48 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2"></div>
          
          {/* Metadata skeleton */}
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
              <div className="w-16 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
            </div>
            <div className="w-12 h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
          </div>
          
          {/* Action button skeleton */}
          <div className="mt-3">
            <div className="w-20 h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

const EnhancedActivityFeed = ({ onViewAll, initialActivities = [] }) => {
  const [filter, setFilter] = useState('all');
  const [activities, setActivities] = useState(initialActivities);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const authContext = useContext(AuthContext);
  const { userProfile } = authContext;
  const navigate = useNavigate();

  // Fetch activities from Supabase
  useEffect(() => {
    const fetchActivities = async () => {
      if (!userProfile?.id) return;
      
      try {
        setLoading(true);
        
        // Determine the correct table based on user role
        const tableName = userProfile.role === 'employer' 
          ? 'employer_activities' 
          : 'va_activities';
        
        // Determine the correct ID field based on user role
        const idField = userProfile.role === 'employer' 
          ? 'employer_id' 
          : 'va_id';
        
        // Use the correct ordering column based on table
        const orderColumn = userProfile.role === 'employer' 
          ? 'activity_date' 
          : 'timestamp';

        // Select only consumed columns per table to reduce payload
        const selectColumns = userProfile.role === 'employer'
          ? 'id, activity_type, description, activity_date, metadata'
          : 'id, type, title, description, timestamp, metadata';

        // The type column name differs between tables
        const typeColumn = userProfile.role === 'employer' ? 'activity_type' : 'type';
        // The message type value differs between tables
        const messageType = userProfile.role === 'employer' ? 'message_received' : 'message';

        // Fetch non-message and message activities separately so high message
        // volume cannot crowd non-message activity types out of the result set.
        const [nonMsgResult, msgResult] = await Promise.all([
          supabase
            .from(tableName)
            .select(selectColumns)
            .eq(idField, userProfile.id)
            .neq(typeColumn, messageType)
            .order(orderColumn, { ascending: false })
            .limit(15),
          supabase
            .from(tableName)
            .select(selectColumns)
            .eq(idField, userProfile.id)
            .eq(typeColumn, messageType)
            .order(orderColumn, { ascending: false })
            .limit(30),
        ]);

        if (nonMsgResult.error) throw nonMsgResult.error;
        if (msgResult.error) throw msgResult.error;

        const data = [...(nonMsgResult.data || []), ...(msgResult.data || [])];

        // Transform data to normalize column names across tables
        const transformedData = data.map(item => {
          if (userProfile.role === 'employer') {
            // Map employer_activities fields to component's expected structure
            const typeLabel = (item.activity_type || 'activity').split('_').map(
              w => w.charAt(0).toUpperCase() + w.slice(1)
            ).join(' ');
            return {
              ...item,
              type: item.activity_type || 'system',
              title: typeLabel,
              description: item.description || '',
              timestamp: item.activity_date,
              metadata: item.metadata || {}
            };
          }
          return item;
        });
        
        setActivities(transformedData);
      } catch (err) {
        console.error('Error fetching activities:', err);
        setError('Failed to load activities');
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();

    // Set up real-time subscription for new activities
    if (!userProfile?.id) return;
    
    // Determine the correct table and ID field based on user role
    const tableName = userProfile.role === 'employer' 
      ? 'employer_activities' 
      : 'va_activities';
    
    const idField = userProfile.role === 'employer' 
      ? 'employer_id' 
      : 'va_id';
    
    // Helper to normalize employer_activities items
    const transformRealtimeItem = (item) => {
      if (userProfile.role === 'employer') {
        const typeLabel = (item.activity_type || 'activity').split('_').map(
          w => w.charAt(0).toUpperCase() + w.slice(1)
        ).join(' ');
        return {
          ...item,
          type: item.activity_type || 'system',
          title: typeLabel,
          description: item.description || '',
          timestamp: item.activity_date,
          metadata: item.metadata || {}
        };
      }
      return item;
    };

    const channel = supabase
      .channel(`${tableName}_${userProfile.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: tableName,
        filter: `${idField}=eq.${userProfile.id}`
      }, payload => {
        const newItem = transformRealtimeItem(payload.new);
        setActivities(current => [newItem, ...current].slice(0, 50));
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: tableName,
        filter: `${idField}=eq.${userProfile.id}`
      }, payload => {
        const updatedItem = transformRealtimeItem(payload.new);
        setActivities(current => 
          current.map(item => item.id === updatedItem.id ? updatedItem : item)
        );
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: tableName,
        filter: `${idField}=eq.${userProfile.id}`
      }, payload => {
        setActivities(current => 
          current.filter(item => item.id !== payload.old.id)
        );
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userProfile?.id, userProfile?.role]);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'application':
      case 'application_received':
        return 'Send';
      case 'match':
        return 'Heart';
      case 'message':
      case 'message_sent':
      case 'message_received':
        return 'MessageSquare';
      case 'profile_view':
      case 'candidate_viewed':
        return 'Eye';
      case 'skill_assessment':
        return 'Award';
      case 'interview':
      case 'interview_scheduled':
      case 'interview_rescheduled':
      case 'interview_cancelled':
        return 'Video';
      case 'job_save':
        return 'Bookmark';
      case 'job_posted':
        return 'Briefcase';
      case 'contract_started':
        return 'UserCheck';
      case 'contract_ended':
        return 'UserX';
      case 'recommendation':
        return 'ThumbsUp';
      default:
        return 'Bell';
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'application':
      case 'application_received':
        return 'text-primary bg-primary-100';
      case 'match':
        return 'text-error bg-error-100';
      case 'message':
      case 'message_sent':
      case 'message_received':
        return 'text-accent bg-accent-100';
      case 'profile_view':
      case 'candidate_viewed':
        return 'text-success bg-success-100';
      case 'skill_assessment':
        return 'text-warning bg-warning-100';
      case 'interview':
      case 'interview_scheduled':
      case 'interview_rescheduled':
      case 'interview_cancelled':
        return 'text-secondary bg-secondary-100';
      case 'job_save':
        return 'text-blue-700 bg-blue-100';
      case 'job_posted':
        return 'text-indigo-700 bg-indigo-100';
      case 'contract_started':
        return 'text-warning bg-warning-100';
      case 'contract_ended':
        return 'text-error bg-error-100';
      case 'recommendation':
        return 'text-purple-700 bg-purple-100';
      default:
        return 'text-text-secondary bg-secondary-100';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - activityTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return activityTime.toLocaleDateString();
  };

  // Get action button based on activity type
  const getActionButton = (activity) => {
    switch (activity.type) {
      case 'application':
        return {
          label: 'View Status',
          icon: 'ExternalLink',
          onClick: () => navigate('/applications'),
        };
      case 'match': {
        const jobId = activity.metadata?.jobId;
        const safeJobId = typeof jobId === 'string' && /^[a-zA-Z0-9-]+$/.test(jobId) ? jobId : '';
        if (!safeJobId) return null;
        return {
          label: 'View Job',
          icon: 'ExternalLink',
          onClick: () => navigate(`/job/${safeJobId}`),
        };
      }
      case 'message':        return {
          label: 'Reply',
          icon: 'MessageCircle',
          onClick: () => navigate('/messages'),
        };
      case 'profile_view':
        return {
          label: 'View Profile',
          icon: 'User',
          onClick: () => navigate('/va-profile'),
        };
      case 'interview':
        return {
          label: 'Prepare',
          icon: 'Calendar',
          onClick: () => navigate('/interviews'),
        };
      default:
        return null;
    }
  };

  // Deduplicate message activities from the same sender, keeping only the latest
  const deduplicatedActivities = (() => {
    const latestMessageBySender = new Map();
    const nonMessageActivities = [];

    activities.forEach((activity) => {
      if (activity.type === 'message' && activity.metadata?.sender_id) {
        const senderId = activity.metadata.sender_id;
        const existing = latestMessageBySender.get(senderId);
        if (!existing || new Date(activity.timestamp) > new Date(existing.timestamp)) {
          latestMessageBySender.set(senderId, activity);
        }
      } else {
        nonMessageActivities.push(activity);
      }
    });

    return [...nonMessageActivities, ...latestMessageBySender.values()]
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  })();

  // Filter activities based on selection, capped to 10 for display
  const filteredActivities = (filter === 'all' 
    ? deduplicatedActivities 
    : deduplicatedActivities.filter(activity => activity.type === filter)
  ).slice(0, 10);

  // Show loading state with skeleton
  if (loading && activities.length === 0) {
    return (
      <div className="card p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-text-primary">Your Activity</h3>
          <div className="w-12 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded animate-pulse"></div>
        </div>
        
        {/* Filter dropdown skeleton */}
        <div className="mb-4 flex items-center gap-2">
          <div className="w-14 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded animate-pulse"></div>
          <div className="w-32 h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded animate-pulse"></div>
        </div>
        
        {/* Activity items skeleton */}
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <ActivityItemSkeleton key={i} delay={i * 150} />
          ))}
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="card p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-text-primary">Your Activity</h3>
        </div>
        <div className="text-center py-8 text-error">
          <Icon name="AlertCircle" size={32} className="mx-auto mb-2" />
          <p>{error}</p>
          <Button 
            size="sm" 
            variant="outline" 
            className="mt-2"
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-text-primary">Recent Activity</h3>
        <button
          onClick={onViewAll}
          className="text-sm text-accent hover:text-accent-600 transition-colors duration-200 cursor-pointer"
        >
          View All
        </button>
      </div>

      {/* Filter dropdown */}
      <div className="mb-4 relative">
        <div className="flex items-center gap-2">
          <label htmlFor="activity-filter" className="text-xs text-text-secondary font-medium">Filter by:</label>
          <div className="relative">
            <select
              id="activity-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="appearance-none bg-white border border-gray-200 rounded-md py-1.5 pl-3 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary cursor-pointer"
            >
              <option value="all">All Activities</option>
              <option value="application">Applications</option>
              <option value="interview">Interviews</option>
              <option value="message">Messages</option>
              <option value="profile_view">Profile Views</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
              <Icon name="ChevronDown" size={14} className="text-gray-500" />
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4 h-80 overflow-y-auto overflow-x-hidden pr-2">
        {filteredActivities.map((activity) => (
          <div key={activity.id} className="p-3 rounded-lg border border-gray-100 hover:border-primary-100 hover:bg-gray-50 transition-colors">
            <div className="flex items-start space-x-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getActivityColor(activity.type)}`}>
                <Icon 
                  name={getActivityIcon(activity.type)} 
                  size={16} 
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-text-primary line-clamp-1">
                    {activity.type === 'message' && activity.metadata?.sender_name
                      ? activity.metadata.sender_name
                      : activity.title}
                  </p>
                  <span className="text-xs text-text-muted flex-shrink-0 ml-2">
                    {formatTimeAgo(activity.timestamp)}
                  </span>
                </div>
                
                <p className="text-sm text-text-secondary mt-1">
                  {activity.description}
                </p>
                
                {activity.metadata && (
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    {activity.metadata.company && (
                      <div className="flex items-center gap-1">
                        <Icon name="Building" size={12} className="text-text-muted" />
                        <span className="text-xs text-text-muted">{activity.metadata.company}</span>
                      </div>
                    )}
                    {activity.metadata.amount && (
                      <div className="flex items-center gap-1">
                        <Icon name="DollarSign" size={12} className="text-success" />
                        <span className="text-xs text-success font-medium">{activity.metadata.amount}</span>
                      </div>
                    )}
                    {activity.metadata.status && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        activity.metadata.status === 'approved' ? 'bg-success-100 text-success-700' :
                        activity.metadata.status === 'pending'? 'bg-warning-100 text-warning-700' : 'bg-error-100 text-error-700'
                      }`}>
                        {activity.metadata.status}
                      </span>
                    )}
                  </div>
                )}
                
                {/* Action button for the activity */}
                {(() => {
                  const action = getActionButton(activity);
                  return action ? (
                    <div className="mt-3">
                      <Button
                        size="xs"
                        variant="outline"
                        iconName={action.icon}
                        onClick={action.onClick}
                      >
                        {action.label}
                      </Button>
                    </div>
                  ) : null;
                })()}
              </div>
            </div>
          </div>
        ))}
        
        {/* Show additional skeleton items when loading more data */}
        {loading && activities.length > 0 && (
          <>
            {[1, 2].map((i) => (
              <ActivityItemSkeleton key={`loading-${i}`} delay={i * 100} />
            ))}
          </>
        )}
      </div>

      {filteredActivities.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Activity" size={32} className="text-text-muted mx-auto mb-2" />
          <p className="text-sm text-text-secondary">
            {filter === 'all' 
              ? 'No recent activity' 
              : `No ${filter} activity found`}
          </p>
          <p className="text-xs text-text-muted mt-1">
            Your activities will appear here as you use the platform
          </p>
        </div>
      )}
    </div>
  );
};

export default EnhancedActivityFeed;

